// frontend/src/pages/SharedSurveyPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import CommentsSection from "../components/CommentsSection";
import { useSocket } from "../contexts/SocketContext";
import "./SharedSurveyPage.css";

// ----- Translation Core Languages -----
const CORE_LANGUAGES = [
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "zh", label: "Chinese (Simplified)" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
];

// ----- Helper: Retrieve translation for a given language -----
// This works whether survey.translations is a Map or a plain object.
const getTranslationForLang = (survey, lang) => {
  if (!survey || !survey.translations) return null;
  return typeof survey.translations.get === "function"
    ? survey.translations.get(lang)
    : survey.translations[lang] || null;
};

const SharedSurveyPage = () => {
  const { token } = useParams();
  const socket = useSocket();

  const [survey, setSurvey] = useState(null);
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track which question's comment box is open
  const [activeQuestion, setActiveQuestion] = useState(null);

  // Reviewer info
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [reviewerColor, setReviewerColor] = useState("#4535bf");

  // ----- Translation-specific state -----
  const [selectedLanguages, setSelectedLanguages] = useState([]); // array of language codes
  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);

  // languageOrder always includes "en" as the base language.
  const languageOrder = useMemo(() => ["en", ...selectedLanguages], [selectedLanguages]);

  // Prepopulate selectedLanguages from stored translations.
  useEffect(() => {
    if (survey && survey.translations) {
      let langs = [];
      if (typeof survey.translations.get === "function") {
        langs = Array.from(survey.translations.keys());
      } else {
        langs = Object.keys(survey.translations);
      }
      setSelectedLanguages(langs);
    }
  }, [survey]);

  // Determine the currently active language.
  const currentLanguage = languageOrder[currentLanguageIndex] || "en";
  const currentTranslation = currentLanguage !== "en" ? getTranslationForLang(survey, currentLanguage) : null;
  const displayTitle =
    currentLanguage === "en"
      ? survey?.title || "Untitled Survey"
      : currentTranslation && currentTranslation.title
      ? currentTranslation.title
      : survey?.title;
  const displayDescription =
    currentLanguage === "en"
      ? survey?.description || "No description available."
      : currentTranslation && currentTranslation.description
      ? currentTranslation.description
      : survey?.description;

  // 1) Fetch Survey & Comments
  useEffect(() => {
    const fetchSurveyData = async () => {
      try {
        const surveyResp = await axiosInstance.get(`/surveys/shared/${token}`);
        const surveyData = surveyResp.data.survey;
        if (!surveyData) {
          setError("Survey not found.");
          setLoading(false);
          return;
        }
        setSurvey(surveyData);

        // Fetch comments
        const commentsResp = await axiosInstance.get(`/surveys/shared/${token}/comments`);
        const commentsData = Array.isArray(commentsResp.data.comments)
          ? commentsResp.data.comments
          : [];

        // Group comments by questionId
        const grouped = {};
        commentsData.forEach((c) => {
          if (!grouped[c.questionId]) grouped[c.questionId] = [];
          grouped[c.questionId].push(c);
        });
        setComments(grouped);
      } catch (fetchError) {
        console.error("Error fetching shared survey:", fetchError);
        setError("Failed to load the shared survey.");
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyData();
  }, [token]);

  // 2) Socket: Listen for new comments
  useEffect(() => {
    if (!socket) return;

    const handleNewComment = (data) => {
      if (data.surveyId === survey?._id) {
        setComments((prev) => {
          const updated = { ...prev };
          if (!updated[data.questionId]) {
            updated[data.questionId] = [];
          }
          updated[data.questionId].push({
            questionId: data.questionId,
            commentText: data.commentText,
            commenterName: data.commenterName,
            createdAt: data.createdAt,
          });
          return updated;
        });
      }
    };

    socket.on("newComment", handleNewComment);
    return () => {
      socket.off("newComment", handleNewComment);
    };
  }, [socket, survey]);

  // 3) Lock/unlock scrolling based on whether a name is entered
  useEffect(() => {
    if (!reviewerName.trim()) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [reviewerName]);

  // 4) Add Comment handler
  const handleAddComment = async (questionId, commentText) => {
    if (!commentText.trim()) return;
    try {
      await axiosInstance.post(`/surveys/shared/${token}/comments`, {
        questionId,
        commentText,
        commenterName: reviewerName || "Anonymous",
      });
      const newComment = {
        questionId,
        commentText,
        commenterName: reviewerName || "Anonymous",
        createdAt: new Date().toISOString(),
        isRead: false,
      };
      setComments((prev) => ({
        ...prev,
        [questionId]: [...(prev[questionId] || []), newComment],
      }));
    } catch (err) {
      console.error("Error adding comment:", err);
    }
  };

  // ----- Language navigation handlers -----
  const handlePreviousLanguage = () => {
    setCurrentLanguageIndex((prev) =>
      prev === 0 ? languageOrder.length - 1 : prev - 1
    );
  };
  const handleNextLanguage = () => {
    setCurrentLanguageIndex((prev) =>
      prev === languageOrder.length - 1 ? 0 : prev + 1
    );
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!survey) return <p>No survey found or invalid token.</p>;

  return (
    <div className="shared-survey-page">
      <Helmet>
        <title>
          {survey
            ? `${survey.title} | Shared Survey | survai`
            : "Loading..."}
        </title>
        <meta
          name="description"
          content={
            survey
              ? survey.description || "Access this shared survey on survai."
              : "Loading survey..."
          }
        />
      </Helmet>
      <div className="app-container">
        {/* ----- Header (sticky at top) ----- */}
        <div className="header-section">
          <img
            src="/survailogo.svg"
            alt="survai Logo"
            className="survey-logo"
          />
          {/* Display translated title and description if available */}
          <h1>{displayTitle}</h1>
          <p>{displayDescription}</p>
          <h3>Please enter your name before viewing the survey</h3>
          <div className="reviewer-inputs">
            <div className="field-group">
              <label>Name (required):</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                required
              />
            </div>
            <div className="field-group">
              <label>Get notified on updates via email (optional):</label>
              <input
                type="email"
                placeholder="Enter your email (optional)"
                value={reviewerEmail}
                onChange={(e) => setReviewerEmail(e.target.value)}
              />
            </div>
          </div>
          {/* Translation Navigation UI */}
          {languageOrder.length > 1 && (
            <div className="translation-nav">
              <button onClick={handlePreviousLanguage} aria-label="Previous language">
                <i className="fa-solid fa-arrow-left"></i>
              </button>
              <span className="language-label">
                {currentLanguage === "en"
                  ? "English"
                  : CORE_LANGUAGES.find((l) => l.code === currentLanguage)?.label ||
                    currentLanguage.toUpperCase()}
              </span>
              <button onClick={handleNextLanguage} aria-label="Next language">
                <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          )}
        </div>

        {/* ----- Survey content below the header ----- */}
        <div className="survey-area">
          <div className="survey-wrapper">
            {survey.sections.map((section, idx) => {
              const translatedSection =
                currentLanguage !== "en" &&
                getTranslationForLang(survey, currentLanguage) &&
                getTranslationForLang(survey, currentLanguage).sections
                  ? getTranslationForLang(survey, currentLanguage).sections[idx]
                  : null;
              const sectionTitle = translatedSection
                ? translatedSection.sectionTitle
                : section.sectionTitle;
              const sectionSummary = translatedSection
                ? translatedSection.sectionSummary
                : section.sectionSummary;
              return (
                <div key={section._id} className="section">
                  <h2>{sectionTitle}</h2>
                  {sectionSummary && (
                    <p className="section-summary-shared">{sectionSummary}</p>
                  )}
                  {section.questions.map((question, index) => {
                    const translatedQuestionText =
                      translatedSection &&
                      translatedSection.questions &&
                      translatedSection.questions[index]
                        ? translatedSection.questions[index].questionText
                        : question.questionText;
                    const translatedAnswerOptions =
                      translatedSection &&
                      translatedSection.questions &&
                      translatedSection.questions[index] &&
                      translatedSection.questions[index].answerOptions &&
                      translatedSection.questions[index].answerOptions.length > 0
                        ? translatedSection.questions[index].answerOptions
                        : question.answerOptions;
                    return (
                      <div key={question._id} className="question-container">
                        <p className="question-text">
                          <strong>Q{question.number}:</strong> {translatedQuestionText}
                        </p>
                        <p className="question-type">
                          Type: {question.questionType}
                        </p>
                        <ul className="answer-options-list">
                          {translatedAnswerOptions.map((option, oIdx) => {
                            const optionText =
                              typeof option === "string" ? option : option.optionText;
                            return (
                              <li key={oIdx} className="answer-option-item">
                                {optionText}
                              </li>
                            );
                          })}
                        </ul>
                        {activeQuestion === question._id ? (
                          <CommentsSection
                            questionId={question._id}
                            comments={comments[question._id] || []}
                            onAddComment={handleAddComment}
                            reviewerName={reviewerName}
                            onClose={() => setActiveQuestion(null)}
                          />
                        ) : (
                          <button
                            className="add-comment-button"
                            onClick={() => setActiveQuestion(question._id)}
                          >
                            Add Comment
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {/* Blurred overlay if no name is entered */}
          {!reviewerName.trim() && (
            <div className="survey-locked-overlay">
              <div className="locked-message">
                <p>Please enter your name above to unlock the survey.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedSurveyPage;
